
import {
  FileDocument,
  SerfJob, SerfJobAndManifest, SerfJobManifest, SerfJobType, Time,
} from '@/model';
import Vue from 'vue';
import IngestionStatusBadge from '@/components/IngestionStatusBadge.vue';
import Progress from '@/components/Progress.vue';
import DocumentList from '@/components/DocumentList.vue';
import api from '@/rest-api';
import { titleForRoute } from '@/store/ui-config';
import { RouteNames } from '@/router/route-names';

interface OptionRow {
  label: string;
  value: string;
  documentId?: number;
  ingestId?: number;
}

export default Vue.extend({
  name: 'SerfQueueView',
  components: {
    IngestionStatusBadge, Progress, DocumentList,
  },

  props: {
    loading: { type: Boolean, default: false },
    jobs: { type: Array as () => SerfJob[], default: () => [] },
    createJob: { type: Object as () => Partial<SerfJobAndManifest> | undefined, required: false },
  },

  computed: {
    pageTitle(): string {
      return titleForRoute(this.$route.path) ?? 'Job Queue';
    },
  },

  data() {
    return {
      edit: false,
      editItem: {
        type: undefined as SerfJobType | undefined,
        documents: [] as FileDocument[],
        options: undefined as string | undefined,
      },
      expanded: [],
      headers: [
        {
          text: 'Id', align: 'start', value: 'id',
        },
        { text: 'Status', value: 'status' },
        { text: 'Type', value: 'type' },
        { text: 'Created', value: 'created' },
        { text: 'Started', value: 'started' },
        { text: 'Stopped', value: 'stopped' },
        { text: 'Duration', value: 'duration' },
        { text: 'Priority', value: 'priority' },
        { text: 'Actions', value: 'actions', sortable: false },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },

  watch: {
    createJob(job: Partial<SerfJobAndManifest> | undefined) {
      if (job) {
        const normalizedOptions = this.normalizeOptions(job);
        this.editItem = {
          type: job.job?.type,
          documents: job.manifest?.documents ? [...job.manifest.documents] : [],
          options: normalizedOptions ? JSON.stringify(normalizedOptions) : undefined,
        };
        this.edit = true;
      }
    },
  },

  methods: {
    normalizeOptions(job: Partial<SerfJobAndManifest>): Record<string, unknown> | undefined {
      if (job.job?.type !== 'ingest') {
        return job.manifest?.options as Record<string, unknown> | undefined;
      }
      const raw = job.manifest?.options as Record<string, unknown> | undefined;
      const options = raw && typeof raw === 'object' ? { ...raw } : {};
      const forcedPipeline = typeof this.$route.query.pipeline === 'string'
        ? this.$route.query.pipeline
        : undefined;
      const pipeline = forcedPipeline ?? this.normalizePipeline(options);
      return {
        pipeline,
        ocr: options.ocr ?? false,
        skipSuggest: options.skipSuggest ?? false,
      };
    },
    normalizePipeline(options: Record<string, unknown>): string {
      if (typeof options.pipeline === 'string' && options.pipeline.length > 0) {
        return options.pipeline;
      }
      const pdfIngest = typeof options.pdfIngest === 'string' ? options.pdfIngest : undefined;
      const skipTextract = options.skipTextract === true;
      if (pdfIngest === 'docling') {
        return 'docling';
      }
      if (skipTextract) {
        return 'pdfadapter';
      }
      return 'textract';
    },
    reload() {
      this.$emit('load');
    },
    save() {
      this.$emit('save', {
        type: this.editItem.type,
        documents: this.editItem.documents,
        options: this.editItem.options && JSON.parse(this.editItem.options),
      });
      this.edit = false;
    },
    loadManifest(jobId: number): Promise<SerfJobManifest | undefined> {
      return api.manifest(jobId);
    },
    duplicateJob(jobId: number) {
      this.$emit('duplicateJob', jobId);
    },
    dateTime(t: Time) {
      return t && Time.asString(t, true);
    },
    duration(job: SerfJob): string | undefined {
      if (job.stopped) {
        return new Date(job.stopped - job.started).toISOString().substr(11, 8);
      }
      return undefined;
    },
    documentLink(documentId: number, ingestId?: number) {
      return {
        name: RouteNames.DocumentResult,
        params: { documentId: `${documentId}`, ingestId: ingestId ? `${ingestId}` : undefined },
      };
    },
    optionRows(type: SerfJobType, options: unknown): OptionRow[] {
      const o = (options ?? {}) as Record<string, any>;
      switch (type) {
        case 'ingest':
          return [
            { label: 'Pipeline', value: `${o.pipeline ?? '-'}` },
            { label: 'OCR', value: `${!!o.ocr}` },
            { label: 'Translate', value: `${!!o.translate}` },
            { label: 'Skip suggest', value: `${!!o.skipSuggest}` },
          ];
        case 'suggest':
          return (o.ingestions ?? []).map((i: { documentId: number; ingestId: number }): OptionRow => ({
            label: `Document #${i.documentId}`,
            value: `Ingest #${i.ingestId}`,
            documentId: i.documentId,
            ingestId: i.ingestId,
          }));
        case 'translate':
          return [
            { label: 'Ingest', value: `#${o.ingestId ?? '-'}` },
            ...(o.additionalInstructions ? [{ label: 'Instructions', value: `${o.additionalInstructions}` }] : []),
            { label: 'Include context', value: `${!!o.includeContext}` },
            { label: 'Skip suggest', value: `${!!o.skipSuggest}` },
          ];
        case 'ner':
          return [{ label: 'Ingest', value: `#${o.ingestId ?? '-'}` }];
        case 'describe':
          return [{
            label: `Document #${o.documentId}`,
            value: o.ingestId ? `Ingest #${o.ingestId}` : '-',
            documentId: o.documentId,
            ingestId: o.ingestId,
          }];
        case 'bulk_summary':
          return [
            { label: 'Documents', value: `${(o.specs ?? []).reduce((n: number, s: { members: unknown[] }) => n + s.members.length, 0)}` },
            { label: 'Auto approve', value: `${!!o.autoApprove}` },
            ...(o.additionalInstructions ? [{ label: 'Instructions', value: `${o.additionalInstructions}` }] : []),
          ];
        default:
          return [];
      }
    },
  },

});
